import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslationService } from '../../services/translation.service';

@Component({
    selector: 'app-cookie-consent-modal',
    templateUrl: './cookie-consent-modal.component.html',
    styleUrls: ['./cookie-consent-modal.component.scss'],
    standalone: false
})
export class CookieConsentModalComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Input() language: 'en' | 'fr' = 'en';  // Accept language as input
  currentLang: 'en' | 'fr' = 'en';  // Used for translations

  preferences = {
    functional: false,
    analytics: false,
    performance: false,
    advertisement: false,
    uncategorized: false
  };

  selectedCategory: string | null = null;

  translations = {
    en: {
      modalTitle: 'Customize Consent Preferences',
      modalDescription: 'We use cookies to help you navigate efficiently, remember your preferences, and analyze site traffic. Cookies marked as “Essential” are crucial for the basic functions of the site and are stored in your browser automatically. We also use cookies from third parties to understand how you use our site, remember your preferences, and show you relevant content and ads. These cookies will only be used with your consent. You can choose which types of cookies to allow. Please note that disabling some cookies may impact your overall browsing experience.',
      essential: 'Essential',
      essentialDescription: 'Essential cookies are crucial for the core functions of this website—such as secure login and saving your consent settings. These cookies do not collect any information that can personally identify you.',
      functional: 'Functional',
      functionalDescription: 'Functional cookies enable specific features on the website, such as sharing content on social media, collecting user feedback, and supporting other third-party services.',
      analytics: 'Analytics',
      analyticsDescription: 'Analytics cookies help us understand how visitors engage with the website. They provide insights into metrics like visitor numbers, bounce rates, traffic sources, and more, allowing us to improve site performance and user experience.',
      performance: 'Performance',
      performanceDescription: 'Performance cookies help track and analyze key website metrics, enabling us to enhance the user experience for visitors.',
      advertisement: 'Advertisement',
      advertisementDescription: 'Advertisement cookies deliver personalized ads tailored to your browsing history and help measure how well our advertising campaigns perform.',
      uncategorized: 'Uncategorized',
      uncategorizedDescription: 'Uncategorized cookies are those that are still being analyzed and categorized.',
      rejectAll: 'Reject All',
      save: 'My Preferences',
      acceptAll: 'Accept All',

      cookieyesConsentDescription: 'Remembers your consent preferences for future visits.',
      __hssrcDescription: 'Detects if the browser has been restarted.',
      elementorDescription: 'Enables real-time editing support in WordPress.',
      _cf_bmDescription: 'This cookie, set by Cloudflare, is used to support Cloudflare Bot Management.',
      wpEmojiSettingsSupportsDescription: 'WordPress sets this cookie when a user interacts with emojis on a WordPress site. It helps determine if the user’s browser can display emojis properly.',

      func_cookieDescription: 'Enables share buttons and UI preferences.',
      lidcDescription: 'LinkedIn sets this cookie to facilitate data center selection.',
      UserMatchHistoryDescription: 'LinkedIn sets this cookie for LinkedIn Ads ID syncing.',
      li_gcDescription: 'LinkedIn sets this cookie for storing visitor’s consent regarding using cookies for non-essential purposes.',

      CLIDDescription: 'Microsoft Clarity sets this cookie to store information about how visitors interact with the website.',
      _gaDescription: 'Google Analytics sets this cookie to calculate visitor, session, and campaign data and track site usage.',
      _gidDescription: 'Google Analytics sets this cookie to store information about how visitors use a website.',
      _gat_UADescription: 'Google Analytics sets this cookie to throttle the request rate, limiting data collection on high-traffic sites.',
      _clckDescription: 'Microsoft Clarity sets this cookie to retain the browser’s Clarity User ID and settings exclusive to that website.',

      SRM_BDescription: 'Used by Microsoft Advertising as a unique ID for visitors.',
      MUIDDescription: 'Bing sets this cookie to recognize unique web browsers visiting Microsoft sites.',
      ANONCHKDescription: 'Bing sets this cookie to store a user’s session ID and verify ads’ clicks on the Bing search engine.',
      _cfuvidDescription: 'Description is currently not available.'
    },
    fr: {
      modalTitle: 'Gérer les préférences de cookies',
      modalDescription: 'Pour garantir le bon fonctionnement de ce site et améliorer votre expérience, nous utilisons des cookies. Vous pouvez consulter et gérer vos préférences en matière de cookies par catégorie ci-dessous. Les cookies marqués comme « Essentiels » sont cruciaux pour les fonctions de base du site et sont automatiquement enregistrés dans votre navigateur. Nous utilisons également des cookies tiers pour comprendre comment vous utilisez notre site, mémoriser vos préférences et vous proposer des contenus et des publicités pertinents. Ces cookies ne seront utilisés qu’avec votre consentement. Vous pouvez choisir les types de cookies à autoriser. Veuillez noter que la désactivation de certains cookies peut affecter votre expérience de navigation.',
      essential: 'Essentiel',
      essentialDescription: 'Les cookies essentiels sont cruciaux pour les fonctions de base de ce site, comme la connexion sécurisée et l’enregistrement de vos préférences de consentement. Ces cookies ne collectent aucune information permettant de vous identifier personnellement.',
      functional: 'Fonctionnel',
      functionalDescription: 'Les cookies fonctionnels permettent d’activer des fonctionnalités spécifiques sur le site, comme le partage de contenu sur les réseaux sociaux, la collecte de commentaires des utilisateurs et le support d’autres services tiers.',
      analytics: 'Analytique',
      analyticsDescription: 'Les cookies analytiques permettent de comprendre comment les visiteurs interagissent avec le site Web. Ils fournissent des informations sur des mesures telles que le nombre de visiteurs, les taux de rebond, les sources de trafic et plus encore, ce qui nous permet d’améliorer les performances du site et l’expérience utilisateur.',
      performance: 'Performance',
      performanceDescription: 'Les cookies de performance aident à suivre et à analyser les indicateurs clés du site Web, afin d’améliorer l’expérience des visiteurs.',
      advertisement: 'Publicité',
      advertisementDescription: 'Les cookies publicitaires permettent d’afficher des annonces personnalisées en fonction de votre historique de navigation et aident à mesurer l’efficacité de nos campagnes publicitaires.',
      uncategorized: 'Non classés',
      uncategorizedDescription: 'Les cookies non classés sont encore en cours d’analyse et n’ont pas encore été attribués à une catégorie spécifique.',
      rejectAll: 'Tout Refuser',
      save: 'Mes Préférences',
      acceptAll: 'Tout Accepter',

      cookieyesConsentDescription: 'Mémorise vos préférences de consentement pour les futures visites.',
      __hssrcDescription: 'Détecte si le navigateur a été redémarré.',
      elementorDescription: 'Permet l’édition en temps réel dans WordPress.',
      _cf_bmDescription: 'Ce cookie est utilisé pour la gestion des bots.',
      wpEmojiSettingsSupportsDescription: 'Défini par WordPress pour vérifier si le navigateur prend en charge les emojis.',

      func_cookieDescription: 'Permet les boutons de partage et les préférences UI.',
      lidcDescription: 'Défini par LinkedIn pour faciliter la sélection du centre de données.',
      UserMatchHistoryDescription: 'Défini par LinkedIn pour la synchronisation des identifiants publicitaires.',
      li_gcDescription: 'Défini par LinkedIn pour stocker le consentement des visiteurs concernant les cookies non essentiels.',

      CLIDDescription: 'Défini par Microsoft Clarity pour enregistrer des informations sur les interactions des visiteurs.',
      _gaDescription: 'Défini par Google Analytics pour calculer les statistiques de visite.',
      _gidDescription: 'Défini par Google Analytics pour stocker des informations d’utilisation.',
      _gat_UADescription: 'Défini par Google Analytics pour limiter le taux de requêtes.',
      _clckDescription: 'Défini par Microsoft Clarity pour conserver l’identifiant utilisateur et les paramètres associés.',

      SRM_BDescription: 'Utilisé par Microsoft Advertising comme identifiant unique.',
      MUIDDescription: 'Défini par Bing pour reconnaître les navigateurs uniques.',
      ANONCHKDescription: 'Défini par Bing pour stocker l’ID de session et vérifier les clics publicitaires.',
      _cfuvidDescription: 'Description non disponible.'
    }
  };

  constructor(
    private modalCtrl: ModalController,
    //private translate: TranslateService
    private translationService: TranslationService
  ) {
    // Listen for global language changes
    this.translationService.getLanguageObservable().subscribe(langEvent => {
      const langCode = langEvent;
      if (langCode === 'en' || langCode === 'fr') {
        this.currentLang = langCode;
      }
    });
  }

  ngOnInit() {
    // Initialize language from parent component
    if (this.language === 'fr' || this.language === 'en') {
      this.currentLang = this.language;
    }
  }

  ionViewWillEnter(): void {
    const savedPrefs = localStorage.getItem('cookiePreferences');
    if (savedPrefs) {
      this.preferences = JSON.parse(savedPrefs);
    }

    // NEW: Grab saved language from localStorage
    const savedLang = localStorage.getItem('lang');
    if (savedLang && (savedLang === 'en' || savedLang === 'fr')) {
      this.currentLang = savedLang;
    }
  }

  toggleDetails(category: string): void {
    this.selectedCategory = this.selectedCategory === category ? null : category;
  }

  acceptAll(): void {
  Object.keys(this.preferences).forEach(key => this.preferences[key] = true);
  localStorage.setItem('cookiePreferences', JSON.stringify(this.preferences));
  localStorage.setItem('cookieConsentGiven', 'true'); //  Mark consent as given
  this.modalCtrl.dismiss(this.preferences);
}

rejectAll(): void {
  Object.keys(this.preferences).forEach(key => this.preferences[key] = false);
  localStorage.setItem('cookiePreferences', JSON.stringify(this.preferences));
  localStorage.setItem('cookieConsentGiven', 'true'); //  Mark consent as given
  this.modalCtrl.dismiss(this.preferences);
}

savePreferences(): void {
  localStorage.setItem('cookiePreferences', JSON.stringify(this.preferences));
  localStorage.setItem('cookieConsentGiven', 'true'); //  Mark consent as given
  this.modalCtrl.dismiss(this.preferences);
}

  

  dismissModal(): void {
  const prefs = localStorage.getItem('cookiePreferences');

  if (!prefs) {
    this.closed.emit(); // Tell parent to show the mini popup
  }

  this.modalCtrl.dismiss(null, 'minimize');
}

}
